import { signUp } from "aws-amplify/auth";
import { FormEvent, useState } from "react";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";

import { validate } from "email-validator";

import { EmailVerificationFields } from "@/components/auth/email-verification-fields";
import { SelectedCellRegion } from "@/components/auth/selected-cell-region";
import { Button } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatAuthError } from "@/lib/auth-errors";
import {
  cellConfig,
  configureAmplifyForActiveCell,
  getCellAuthConfigStatus,
  hasActiveCellId,
} from "@/lib/cell";

export const Route = createFileRoute("/sign-up")({
  beforeLoad: () => {
    if (!hasActiveCellId()) {
      throw redirect({
        to: "/region",
        search: { next: "/sign-up" },
      });
    }
  },
  component: BrowserSignUp,
});

interface CustomElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface CustomForm extends HTMLFormElement {
  readonly elements: CustomElements;
}

function BrowserSignUp() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<CustomForm>) => {
    event.preventDefault();
    setFormError(null);

    const emailRaw = event.currentTarget.elements.email.value;
    const email = emailRaw.trim().toLowerCase();

    const password = event.currentTarget.elements.password.value;

    if (!validate(email) || password.length < 8) {
      setFormError(
        "Use a valid email and a password at least eight characters.",
      );
      return;
    }

    setIsLoading(true);
    try {
      configureAmplifyForActiveCell();
      const authReady = getCellAuthConfigStatus();
      if (!authReady.ok) {
        setFormError(authReady.message);
        setIsLoading(false);
        return;
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
        },
      });

      if (
        result.isSignUpComplete ||
        result.nextStep?.signUpStep === "DONE"
      ) {
        navigate({ to: "/sign-in", replace: true });
        return;
      }

      setPendingEmail(email);
    } catch (err) {
      setFormError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  cellConfig();

  if (pendingEmail) {
    return (
      <main className="flex flex-col items-center justify-center gap-8 min-h-screen px-6">
        <img src="/deflow.png" alt="Deflow logo" width={180} height={37} />
        <SelectedCellRegion />

        <div className="w-full max-w-sm">
          <EmailVerificationFields
            email={pendingEmail}
            prompt="We sent a verification code to your email. Enter it below to finish creating your account."
            onVerified={() => navigate({ to: "/sign-in", replace: true })}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="flex flex-col items-center justify-center gap-8 min-h-screen px-6">
      <img src="/deflow.png" alt="Deflow logo" width={180} height={37} />
      <SelectedCellRegion />

      <form className="w-full max-w-sm space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" autoComplete="email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
          />
        </div>
        {formError ? (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        ) : null}
        <Button className="w-full" type="submit" disabled={isLoading}>
          {isLoading ? (
            <Icons.spinner className="h-4 w-4 animate-spin" />
          ) : (
            "Create account"
          )}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground">
        Already registered?{" "}
        <Link to="/sign-in" className="underline">
          Sign in
        </Link>
      </p>
    </main>
  );
}
